import React, { useContext, useState } from 'react'
import {Categories, CollectionPeriods, CreateCollectionPayload, CreateListPayload, Media, TabItem } from '../../types/SharedTypes';
import { useCreateNewCollection, useCreateNewList, useCreateListSet } from '../../api/data-access';
import TabBar from '../navigation/TabBar';
import Modal from '../Modal';
import { CloseIcon } from '../../assets/icons';
import { useNavigate } from 'react-router';
import CreateListTab from './CreateListTab';
import CreateCollectionTab from './CreateCollectionTab';
import LoadingIcon from '../shared/LoadingIcon';
import useUploadMedia from '../../hooks/useUploadMedia';
import { ToastContext } from '../../contexts/ToastContext';
import CreateListSetTab from './CreateListSetTab';
import { capitalize } from 'lodash';

export type CreateListForm = {
  title: string;
  description?: string;
  ascending: boolean;
  isPublic: boolean;
  ordered: boolean;
  category: Categories | '';
  thumbnailEntry?: number;
  subCategories?: string[];
  image?: string;
  isSet?: boolean;
} & Partial<CreateSetForm>;

export type CreateCollectionForm = {
  title: string;
  description?: string;
  isPublic: boolean;
  category: Categories | '';
  subCategories?: string[];
  image?: string;
  isSet?: boolean;
} & Partial<CreateSetForm>;

export type CreateSetForm = {
  period: CollectionPeriods;
  startingKey?: number;
  isSetDisabled?: boolean;
  type: 'list' | 'collection';
}
type Tabs = "list" | "collection" | "set";

type Props = {
  setModalVisible: (prev: boolean) => void;
  forCollection?: string;
}

const CreateListModal = ({setModalVisible, forCollection}: Props) => {
  
  const {requestToast} = useContext(ToastContext);
  
  const navigate = useNavigate();
  const createList = useCreateNewList();
  const createCollection = useCreateNewCollection();
  const {handleMedia} = useUploadMedia();
  
  const [listForm, setListForm] = useState<CreateListForm>({
    title: "",
    description: "",
    ascending: true,
    ordered: true,
    category: "",
    subCategories: [],
    isPublic: true,
    thumbnailEntry: 3
  });
  const [collectionForm, setCollectionForm] = useState<CreateCollectionForm>({
    title: "",
    description: "",
    category: "",
    subCategories: [],
    isPublic: true,
  });
  const [listSetForm, setListSetForm] = useState<CreateSetForm>({
    period: 'weekly',
    startingKey: 1,
    type: 'list'
  });
  const [newListImage, setNewListImage] = useState<File>();
  const [newCollectionImage, setNewCollectionImage] = useState<File>();
  
  const isListValid = !!(forCollection 
    ? (listForm.title) 
    : (listForm.title && listForm.category)
  );
  const isCollectionValid = !!(collectionForm.title && collectionForm.category);
  const isSetValid = listSetForm.startingKey && listSetForm.type === 'collection' ? isCollectionValid : isListValid;
  
  const [currentTab, setCurrentTab] = useState<Tabs>("list");
  
  const tabs: TabItem[] = forCollection ? [
    {title: "List", onClick: () => setCurrentTab("list")},
  ] : [
    {title: "List", onClick: () => setCurrentTab("list")},
    {title: "Collection", onClick: () => setCurrentTab("collection")},
    {title: "Series", onClick: () => setCurrentTab("set")}
  ];
  
  const validateAndPost = () => {
    if ((currentTab === "list" || (currentTab === 'set' && listSetForm.type === 'list')) && isListValid) handlePostList();
    else if ((currentTab === "collection" || (currentTab === 'set' && listSetForm.type === 'collection')) && isCollectionValid) handlePostCollection();
  }
  
  const handlePostList = async () => {

    let listSet: string | undefined = undefined;

    if (currentTab === 'set') {
      try {
        listSet = await createListSet.mutateAsync({
          period: listSetForm.period,
          startingKey: listSetForm.startingKey || 1,
          defaults: {
            description: collectionForm.description,
            image: undefined
          }
        });
      } catch (err) {
        requestToast("Failed to create series, try again.", "error");
        return;
      }
    }
    
    const newList: CreateListPayload = {
      type: 'list',
      title: listForm.title,
      description: listForm.description,
      category: listForm.category as Categories,
      subCategories: listForm.subCategories,
      ascending: listForm.ascending,
      ordered: listForm.ordered,
      isPublic: listForm.isPublic,
      isDraft: true,
      parentCollection: forCollection,
      listSet
    }
    
    if (newListImage) {
      const newMedia = await handleMedia(newListImage);
      if (!newMedia) {
        requestToast('Failed to upload list image.', 'error');
        return;
      }
      Object.assign(newList, {image: newMedia.url});
    }
    console.log(newList);
    
    createList.mutate(newList, {
      onSuccess(data) {
        if (!data) return;
        setModalVisible(false);
        if (!forCollection) navigate(`/creator/${data._id}`);
      },
    });
  }
  
  const createListSet = useCreateListSet();
  
  const handlePostCollection = async () => {
    if (!collectionForm.category) return;

    let listSet: string | undefined = undefined;

    if (currentTab === 'set') {
      try {
        listSet = await createListSet.mutateAsync({
          period: listSetForm.period,
          startingKey: listSetForm.startingKey || 1,
          defaults: {
            description: collectionForm.description,
            image: undefined
          }
        });
      } catch (err) {
        requestToast("Failed to create series, try again.", "error");
        return;
      }
    }
    
    const newCollection: CreateCollectionPayload = {
      type: 'collection',
      title: collectionForm.title,
      description: collectionForm.description,
      category: collectionForm.category,
      subCategories: collectionForm.subCategories,
      isPublic: collectionForm.isPublic,
      listSet,
      isDraft: true
    }
    
    if (newCollectionImage) {
      const newMedia = await handleMedia(newCollectionImage);
      if (!newMedia) {
        requestToast('Failed to upload list image.', 'error');
        return;
      }
      Object.assign(newCollection, {image: newMedia.url});
    }
    
    console.log(newCollection);
    
    createCollection.mutate(newCollection, {
      onSuccess(data, variables, context) {
        if (!data) return;
        setModalVisible(false);
        navigate(`/edit/collection/${data._id}`);
      },
    });
  }
  
  if (createList.isPending || createCollection.isPending) return (
    <Modal>
      <div className='item-list center-horizontal padding-large'>
        <LoadingIcon />
      </div>
    </Modal>
  )

  return (
    <Modal fixedBottomItem>
      <div className='bottom-item-layout no-gap'>
        <div className="item-list no-gap">
          <div className='right-item-layout padding remove-padding-bottom center-horizontal'>
            <h1 className="heading-medium">{forCollection ? 'Add List to Collection' : 'Create'}</h1>
            <button className='icon xs' onClick={() => setModalVisible(false)}>
              <CloseIcon />
            </button>
          </div>
          <TabBar tabData={tabs} sticky />
          {currentTab === "set" && <CreateListSetTab form={listSetForm} setForm={setListSetForm} />}
          {(currentTab === "list" || (currentTab === 'set' && listSetForm.type === 'list')) && 
            <CreateListTab
              forCollection={forCollection}
              form={listForm}
              setForm={setListForm}
              setNewImage={setNewListImage}
              isSet={currentTab === 'set' && listSetForm.type === 'list'}
            />
          }
          {(currentTab === "collection" || (currentTab === 'set' && listSetForm.type === 'collection')) && 
            <CreateCollectionTab
              form={collectionForm}
              setForm={setCollectionForm}
              setNewImage={setNewCollectionImage}
              isSet={currentTab === 'set' && listSetForm.type === 'collection'} 
            />
          }
        </div>
        <div className="padding-small item-list top-border">
          {currentTab === 'list' && <button onClick={validateAndPost} aria-disabled={!isListValid} disabled={!isListValid} className='primary'>{"Create List"}</button>}
          {currentTab === 'collection' && <button onClick={validateAndPost} aria-disabled={!isCollectionValid} disabled={!isCollectionValid} className='primary'>{"Create Collection"}</button>}
          {currentTab === 'set' && <button onClick={validateAndPost} aria-disabled={!isSetValid} disabled={!isSetValid} className='primary'>{`Create ${capitalize(listSetForm.type)} Series`}</button>}
        </div>
      </div>
    </Modal>
  )
}

export default CreateListModal;
